import React, { useState } from 'react';
import { CheckCircle, Clock, BookOpen, Calculator, ChevronRight } from 'lucide-react';
import ImageViewer from '../components/ImageViewer';

// Simplified Card component for PDF (no animations)
const Card = ({ children, className = '' }) => (
  <div 
    className={`bg-white rounded-2xl p-6 border border-gray-200 ${className}`}
    style={{
      breakInside: 'avoid',
      pageBreakInside: 'avoid',
    }}
  >
    {children}
  </div>
);

// Simplified MetricCard component for PDF
const MetricCard = ({ title, value, unit = '', icon: Icon, color = 'text-gray-800' }) => (
  <div 
    className="bg-white p-6 rounded-xl shadow-md flex items-center space-x-4"
    style={{
      breakInside: 'avoid',
      pageBreakInside: 'avoid',
    }}
  >
    {Icon && <Icon size={32} style={{ color: '#a6a6a6' }} />}
    <div>
      <h3 className="text-gray-500 text-sm font-semibold">{title}</h3>
      <p className="text-2xl font-bold text-gray-800">{value}{unit}</p>
    </div>
  </div>
);

// Question Table component for PDF (always expanded, no animations)
const QuestionTable = ({ questions, loading, error }) => {
  const filteredQuestions = questions || [];

  const getResultHeader = (status) => {
    switch (status) {
      case 'correct': return "CORRECT!";
      case 'incorrect': return "INCORRECT";
      case 'unanswered': return "UNANSWERED";
      default: return "Result";
    }
  }
  
  const getResultBorderColor = (status) => {
    switch (status) {
      case 'correct': return 'border-green-500 bg-green-50';
      case 'incorrect': return 'border-red-500 bg-red-50';
      case 'unanswered': return 'border-yellow-500 bg-yellow-50';
      default: return 'border-gray-300 bg-gray-50';
    }
  }
  
  const getResultTextColor = (status) => {
    switch (status) {
      case 'correct': return 'text-green-700';
      case 'incorrect': return 'text-red-700';
      case 'unanswered': return 'text-yellow-700';
      default: return 'text-gray-700';
    }
  }

  const getAnswerOptionKeys = (question) => {
    const answerOptionKeys = ['A', 'B', 'C', 'D'];
    return answerOptionKeys.map(key => {
      const value = question['question_option_' + key.toLowerCase()];
      return value ? { key, value } : null;
    }).filter(opt => opt !== null);
  };

  const renderTextWithPi = (text) => {
    if (!text || !text.includes('π')) return text;
    
    return text.split('π').map((part, index) => (
      <React.Fragment key={index}>
        {part}
        {index < text.split('π').length - 1 && (
          <span className="font-sans">π</span>
        )}
      </React.Fragment>
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[120px] text-gray-500">
        Loading your questions...
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[120px] text-red-500 font-semibold">
        Error fetching questions.
      </div>
    );
  }

  return (
    <div 
      className="bg-white rounded-xl shadow-lg p-4 mt-4"
      style={{
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
      }}
    >
      <table className="min-w-full divide-y divide-gray-200" style={{ height: 'auto' }}>
        <thead>
          <tr>
            <th className="px-2 py-2 text-left text-sm font-semibold text-gray-700">#</th>
            <th className="px-2 py-2 text-left text-sm font-semibold text-gray-700">Status</th>
            <th className="px-2 py-2 text-left text-sm font-semibold text-gray-700">Subject</th>
            <th className="px-2 py-2 text-left text-sm font-semibold text-gray-700">Type</th>
            <th className="px-2 py-2 text-left text-sm font-semibold text-gray-700">Difficulty</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {filteredQuestions.length === 0 ? (
            <tr><td colSpan={5} className="text-center text-gray-400 py-4">No questions found.</td></tr>
          ) : filteredQuestions.map((q, idx) => (
            <React.Fragment key={q.question_id || q.id}>
              <tr className="bg-white">
                <td className="px-2 py-2 text-gray-500 font-semibold">{idx + 1}</td>
                <td className="px-2 py-2">
                  <span className={
                    (q.question_status || q.status || '').toLowerCase() === 'correct' ? 'text-green-600 font-semibold' :
                      (q.question_status || q.status || '').toLowerCase() === 'incorrect' ? 'text-red-500 font-semibold' :
                        'text-yellow-400 font-semibold'
                  }>{q.question_status || q.status}</span>
                </td>
                <td className="px-2 py-2 text-gray-800">{q.question_content === 'Sentence Completion' ? 'Vocablary In Context' : q.question_content}</td>
                <td className="px-2 py-2 text-gray-800">{q.question_type || q.type}</td>
                <td className="px-2 py-2 text-gray-800">{q.question_difficulty || q.difficulty}</td>
              </tr>
              <tr>
                <td 
                  colSpan={5} 
                  className="bg-gray-50 px-4 border-t border-gray-200"
                  style={{
                    breakInside: 'avoid',
                    pageBreakInside: 'avoid',
                  }}
                >
                  <div className="py-6">
                    <div className="md:flex md:gap-8">
                      {/* Left Side: Question & Feedback */}
                      <div className="md:w-3/5 lg:w-1/2 space-y-5">
                        <h1 className="text-xl md:text-2xl font-bold text-gray-800 font-zonaProUltra mb-1">Question:</h1>
                        {q.image_name && (
                          <div className="my-4 rounded-lg overflow-hidden shadow max-h-60 flex justify-center items-center bg-gray-50">
                            <ImageViewer fileName={q.image_name} alt="Question image" className="max-h-full max-w-full object-contain" />
                          </div>
                        )}
                        <p className="text-base md:text-lg text-gray-700 text-left leading-relaxed whitespace-pre-line">
                          {renderTextWithPi(q.question_text || q.question_choice || 'N/A')}
                        </p>
                        <div className={`mt-4 p-4 rounded-lg border-l-4 ${getResultBorderColor(q.question_status)}`}>
                          <h3 className={`text-lg font-semibold mb-2 font-zonaProUltra ${getResultTextColor(q.question_status)}`}>
                            {getResultHeader(q.question_status)}
                          </h3>
                          <p className={`mb-2 ${getResultTextColor(q.question_status)}`}>{q.question_feedback || 'N/A'}</p>
                          {q.question_status !== 'unanswered' && q.points !== undefined && (
                            <div className={`text-base font-semibold ${getResultTextColor(q.question_status)}`}>
                              Points: <span className="font-bold">{q.points > 0 ? `+${q.points}` : q.points}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right Side: Answer Options */}
                      <div className="md:w-2/5 lg:w-1/2 md:pl-4 lg:pl-6 mt-6 md:mt-0">
                        <h2 className="text-lg sm:text-xl font-zonaProUltra text-gray-700 mb-4">
                          Results:
                        </h2>
                        {q.question_type === 'Grid-in' ? (
                          <div className="space-y-3">
                            {q.question_status === 'correct' && (
                              <div className="w-full text-left p-3 rounded-lg border-2 font-medium bg-green-50 border-green-500 text-green-700">
                                <span className="font-bold mr-2">Your Answer:</span>{renderTextWithPi(q.question_selected_option || 'N/A')}
                              </div>
                            )}

                            {q.question_status === 'incorrect' && (
                              <>
                                <div className="w-full text-left p-3 rounded-lg border-2 font-medium bg-red-50 border-red-500 text-red-700">
                                  <span className="font-bold mr-2">Your Answer:</span>{renderTextWithPi(q.question_selected_option || 'N/A')}
                                </div>
                                <div className="w-full text-left p-3 rounded-lg border-2 font-medium bg-green-50 border-green-500 text-green-700">
                                  <span className="font-bold mr-2">Correct Answer:</span>{renderTextWithPi(q.question_answer || 'N/A')}
                                </div>
                              </>
                            )}

                            {q.question_status === 'unanswered' && (
                              <div className="w-full text-left p-3 rounded-lg border-2 font-medium bg-green-50 border-green-500 text-green-700">
                                <span className="font-bold mr-2">Correct Answer:</span>{renderTextWithPi(q.question_answer || 'N/A')}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {getAnswerOptionKeys(q).map((opt) => {
                              const isUserAnswer = q.question_selected_option === opt.value;
                              const isCorrectAnswer = q.question_answer === opt.value;

                              let classes = "w-full text-left p-3 rounded-lg border-2 font-medium";

                              if (isUserAnswer && !isCorrectAnswer) {
                                classes += ' bg-red-50 border-red-400 text-red-700';
                              } else if (isCorrectAnswer) {
                                classes += ' bg-green-50 border-green-500 text-green-700';
                              } else {
                                classes += ' bg-gray-100 border-gray-300 text-gray-500 opacity-75';
                              }

                              return (
                                <div key={opt.key} className={classes}>
                                  <span className="font-bold mr-2">{opt.key})</span>{renderTextWithPi(opt.value)}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Utility to format seconds as hr/min/sec
function formatDuration(seconds) {
  if (seconds == null) return 'N/A';
  seconds = Math.round(seconds);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  let result = '';
  if (h > 0) result += `${h} hr${h > 1 ? 's' : ''} `;
  if (m > 0) result += `${m} min${m > 1 ? 's' : ''} `;
  if (h === 0 && m === 0) result += `${s} sec${s !== 1 ? 's' : ''}`;
  return result.trim();
}

const QUESTION_TABS = [
  { key: 'english1', label: 'English Module 1' },
  { key: 'english2', label: 'English Module 2' },
  { key: 'math1', label: 'Math Module 1' },
  { key: 'math2', label: 'Math Module 2' },
];

// Main PDF Layout Component
const SATTestReportPDFLayout = ({ mappedData, questionsByTab, topicTotalCount, barWidths }) => {
  // Ensure mappedData is available before rendering
  if (!mappedData) return <div className="text-center p-10">Loading report data for PDF...</div>;

  return (
    <div className="bg-white p-6 w-full font-zonaProRegular">
      <div className="relative z-10" id="sat-test-report-content">
        {/* Main Report Content */}
        <div id="main-report-content">
          <h1
            className="text-4xl lg:text-5xl font-bold font-zonaProUltra mb-10 text-center relative inline-block w-full"
            style={{
              color: '#212D82',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            SAT Practice Test Results
          </h1>

          {/* Overall Score Section */}
          <Card 
            className="mb-10"
            style={{
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col items-center">
                <h2 className="text-xl font-bold text-gray-700 mb-4 font-zonaProRegular">Total SAT Score</h2>
                <p className="text-5xl lg:text-6xl font-extrabold mb-2" style={{ color: '#ff7a00' }}>
                  {mappedData.overallScore} / {mappedData.maxScore}
                </p>
                <p className="text-gray-500 text-lg mb-4">Test Date: {mappedData.testDate}</p>

                <div className="flex flex-row justify-center gap-6 mb-6 w-full max-w-xl">
                  <div className="flex-1 bg-blue-50 p-4 rounded-lg shadow-inner text-center">
                    <p className="text-lg font-semibold text-blue-700">English Section Score</p>
                    <p className="text-4xl font-bold text-blue-600">{mappedData.englishSection.score} / {mappedData.englishSection.maxScore}</p>
                  </div>
                  <div className="flex-1 bg-purple-50 p-4 rounded-lg shadow-inner text-center">
                    <p className="text-lg font-semibold text-purple-700">Math Section Score</p>
                    <p className="text-4xl font-bold text-purple-600">{mappedData.mathSection.score} / {mappedData.mathSection.maxScore}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 content-start">
                <MetricCard
                  title="Overall Accuracy"
                  value={mappedData.overallAccuracyPercentage}
                  unit="%"
                  icon={CheckCircle}
                  color="text-green-600"
                />
                <MetricCard
                  title="Total Time Taken"
                  value={mappedData.totalTimeTakenDisplay}
                  icon={Clock}
                  color="text-indigo-600"
                />

                <div 
                  className="col-span-2 w-full bg-white text-gray-800 p-6 rounded-xl shadow-lg border border-gray-200"
                  style={{
                    breakInside: 'avoid',
                    pageBreakInside: 'avoid',
                  }}
                >
                  <h3 className="text-lg font-semibold text-gray-700 mb-4 font-zonaProRegular">Question Breakdown</h3>
                  <div className="flex w-full h-8 rounded-full overflow-hidden mb-4 bg-gray-200">
                    <div
                      className="bg-green-500 h-full rounded-l-full"
                      style={{ width: `${barWidths?.correct || 0}%` }}
                    />
                    <div
                      className="bg-red-500 h-full"
                      style={{ width: `${barWidths?.incorrect || 0}%` }}
                    />
                    <div
                      className="bg-gray-400 h-full rounded-r-full"
                      style={{ width: `${barWidths?.unanswered || 0}%` }}
                    />
                  </div>
                  <div className="flex justify-around text-center font-bold text-lg">
                    <div className="flex flex-col items-center">
                      <span className="text-green-600">{mappedData.overallCorrect}</span>
                      <span className="text-sm text-gray-700">Correct</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-red-600">{mappedData.overallIncorrect}</span>
                      <span className="text-sm text-gray-700">Incorrect</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-gray-600">{(topicTotalCount?.english + topicTotalCount?.math || 0) - mappedData.overallCorrect - mappedData.overallIncorrect}</span>
                      <span className="text-sm text-gray-700">Unanswered</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* English Section Card */}
          <section 
            className="mb-10"
            style={{
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
            }}
          >
            <Card className="mb-6">
              <h2 className="text-2xl font-bold text-gray-700 mb-4 flex items-center font-zonaProUltra" style={{ color: '#0a1a2f' }}>
                <BookOpen size={28} className="mr-3" style={{ color: '#a6a6a6' }} /> English Section
              </h2>
              <div className="flex flex-col xl:flex-row items-center w-full mb-4">
                <div className="flex flex-col items-center justify-center md:mr-8 min-w-[220px] mb-4 md:mb-0">
                  <p className="text-5xl font-bold mb-2" style={{ color: '#ff7a00' }}>{mappedData.englishSection.score}</p>
                  <span className="text-gray-500 text-lg">Score</span>
                </div>
                <div className="flex-1 flex flex-col gap-4 w-full">
                  <div 
                    className="overflow-x-auto w-full max-w-full"
                    style={{
                      breakInside: 'avoid',
                      pageBreakInside: 'avoid',
                    }}
                  >
                    <table 
                      className="w-full text-center rounded-2xl overflow-hidden border-separate border-spacing-0"
                      style={{
                        breakInside: 'avoid',
                        pageBreakInside: 'avoid',
                      }}
                    >
                      <thead>
                        <tr className="bg-gray-50 text-black text-base font-zonaProRegular font-bold">
                          <th className="py-2 px-2 font-bold text-left"></th>
                          <th className="py-2 px-2 font-bold">Correct</th>
                          <th className="py-2 px-2 font-bold">Incorrect</th>
                          <th className="py-2 px-2 font-bold">Unanswered</th>
                          <th className="py-2 px-2 font-bold">Total</th>
                          <th className="py-2 px-2 font-bold">Accuracy</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="font-bold">
                          <td className="py-2 px-2 text-left text-base text-gray-800">Overall English Performance</td>
                          <td className="py-2 px-2 text-base text-green-600">{mappedData.englishSection.correct}</td>
                          <td className="py-2 px-2 text-base text-red-500">{mappedData.englishSection.incorrect}</td>
                          <td className="py-2 px-2 text-base text-yellow-600">{(topicTotalCount?.english || 0) - mappedData.englishSection.correct - mappedData.englishSection.incorrect}</td>
                          <td className="py-2 px-2 text-base text-gray-800">{topicTotalCount?.english || 0}</td>
                          <td className="py-2 px-2 text-base text-gray-800">{Math.round((mappedData.englishSection.correct / 54) * 100)}%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {mappedData.englishSection.modules.map((module, moduleIndex) => (
                    <div 
                      key={moduleIndex} 
                      className="bg-blue-50 p-4 h-full min-h-[220px] rounded-lg shadow-inner flex flex-col w-full max-w-full"
                      style={{
                        breakInside: 'avoid',
                        pageBreakInside: 'avoid',
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-blue-700">{module.name}</span>
                      </div>
                      <div 
                        className="overflow-x-auto w-full max-w-full"
                        style={{
                          breakInside: 'avoid',
                          pageBreakInside: 'avoid',
                        }}
                      >
                        <table 
                          className="w-full text-center rounded-2xl overflow-hidden border-separate border-spacing-0"
                          style={{
                            breakInside: 'avoid',
                            pageBreakInside: 'avoid',
                          }}
                        >
                          <thead>
                            <tr className="bg-gray-50 text-black text-base font-zonaProRegular font-bold">
                              <th className="py-2 px-2 font-bold text-left">Subtopic</th>
                              <th className="py-2 px-2 font-bold">Correct</th>
                              <th className="py-2 px-2 font-bold">Incorrect</th>
                              <th className="py-2 px-2 font-bold">Unanswered</th>
                              <th className="py-2 px-2 font-bold">Total</th>
                              <th className="py-2 px-2 font-bold">Accuracy</th>
                            </tr>
                          </thead>
                          <tbody>
                            {module.breakdown.map((item, idx) => (
                              <tr key={idx} className="border-b last:border-b-0 border-gray-200">
                                <td className="py-2 px-2 text-left text-base font-medium text-gray-800">{item.type}</td>
                                <td className="py-2 px-2 text-base text-green-600 font-medium">{item.correct}</td>
                                <td className="py-2 px-2 text-base text-red-500 font-medium">{item.incorrect}</td>
                                <td className="py-2 px-2 text-base text-yellow-600 font-medium">{item.unanswered}</td>
                                <td className="py-2 px-2 text-base font-medium text-gray-800">{item.correct + item.incorrect + item.unanswered}</td>
                                <td className="py-2 px-2 text-base font-medium text-gray-800">{item.accuracy}%</td>
                              </tr>
                            ))}
                            <tr className="border-t-2 border-gray-300 font-bold">
                              <td className="py-2 px-2 text-left text-base text-gray-800">Total</td>
                              <td className="py-2 px-2 text-base text-green-600">{module.correct}</td>
                              <td className="py-2 px-2 text-base text-red-500">{module.incorrect}</td>
                              <td className="py-2 px-2 text-base text-yellow-600">{module.totalQuestions - module.correct - module.incorrect}</td>
                              <td className="py-2 px-2 text-base text-gray-800">{module.totalQuestions}</td>
                              <td className="py-2 px-2 text-base text-gray-800">{module.correct + module.incorrect > 0 ? Math.round((module.correct / 27) * 100) : 0}%</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </section>

          {/* Math Section Card */}
          <section 
            className="mb-10"
            style={{
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
            }}
          >
            <Card>
              <h2 className="text-2xl font-bold text-gray-700 mb-4 flex items-center font-zonaProUltra" style={{ color: '#0a1a2f' }}>
                <Calculator size={28} className="mr-3" style={{ color: '#a6a6a6' }} /> Math Section
              </h2>
              <div className="flex flex-col xl:flex-row items-center w-full mb-4">
                <div className="flex flex-col items-center justify-center md:mr-8 min-w-[220px] mb-4 md:mb-0">
                  <p className="text-5xl font-bold mb-2" style={{ color: '#ff7a00' }}>{mappedData.mathSection.score}</p>
                  <span className="text-gray-500 text-lg">Score</span>
                </div>
                <div className="flex-1 flex flex-col gap-4 w-full">
                  <div 
                    className="overflow-x-auto w-full max-w-full"
                    style={{
                      breakInside: 'avoid',
                      pageBreakInside: 'avoid',
                    }}
                  >
                    <table 
                      className="w-full text-center rounded-2xl overflow-hidden border-separate border-spacing-0"
                      style={{
                        breakInside: 'avoid',
                        pageBreakInside: 'avoid',
                      }}
                    >
                      <thead>
                        <tr className="bg-gray-50 text-black text-base font-zonaProRegular font-bold">
                          <th className="py-2 px-2 font-bold text-left"></th>
                          <th className="py-2 px-2 font-bold">Correct</th>
                          <th className="py-2 px-2 font-bold">Incorrect</th>
                          <th className="py-2 px-2 font-bold">Unanswered</th>
                          <th className="py-2 px-2 font-bold">Total</th>
                          <th className="py-2 px-2 font-bold">Accuracy</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="font-bold">
                          <td className="py-2 px-2 text-left text-base text-gray-800">Overall Math Performance</td>
                          <td className="py-2 px-2 text-base text-green-600">{mappedData.mathSection.correct}</td>
                          <td className="py-2 px-2 text-base text-red-500">{mappedData.mathSection.incorrect}</td>
                          <td className="py-2 px-2 text-base text-yellow-600">{(topicTotalCount?.math || 0) - mappedData.mathSection.correct - mappedData.mathSection.incorrect}</td>
                          <td className="py-2 px-2 text-base text-gray-800">{topicTotalCount?.math || 0}</td>
                          <td className="py-2 px-2 text-base text-gray-800">{mappedData.mathSection.totalQuestions > 0 ? Math.round((mappedData.mathSection.correct / 44) * 100) : 0}%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="flex-1 flex flex-col gap-4">
                    {mappedData.mathSection.modules.map((module, moduleIndex) => (
                      <div 
                        key={moduleIndex} 
                        className="bg-purple-50 p-4 h-full min-h-[220px] rounded-lg shadow-inner flex flex-col w-full max-w-full"
                        style={{
                          breakInside: 'avoid',
                          pageBreakInside: 'avoid',
                        }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-purple-700">{module.name}</span>
                        </div>
                        <div 
                          className="overflow-x-auto w-full max-w-full"
                          style={{
                            breakInside: 'avoid',
                            pageBreakInside: 'avoid',
                          }}
                        >
                          <table 
                            className="w-full text-center rounded-2xl overflow-hidden border-separate border-spacing-0"
                            style={{
                              breakInside: 'avoid',
                              pageBreakInside: 'avoid',
                            }}
                          >
                            <thead>
                              <tr className="bg-gray-50 text-black text-base font-zonaProRegular font-bold">
                                <th className="py-2 px-2 font-bold text-left">Subtopic</th>
                                <th className="py-2 px-2 font-bold">Correct</th>
                                <th className="py-2 px-2 font-bold">Incorrect</th>
                                <th className="py-2 px-2 font-bold">Unanswered</th>
                                <th className="py-2 px-2 font-bold">Total</th>
                                <th className="py-2 px-2 font-bold">Accuracy</th>
                              </tr>
                            </thead>
                            <tbody>
                              {module.breakdown.map((item, idx) => (
                                <tr key={idx} className="border-b last:border-b-0 border-gray-200">
                                  <td className="py-2 px-2 text-left text-base font-medium text-gray-800">{item.subTopic || item.type}</td>
                                  <td className="py-2 px-2 text-base text-green-600 font-medium">{item.correct}</td>
                                  <td className="py-2 px-2 text-base text-red-500 font-medium">{item.incorrect}</td>
                                  <td className="py-2 px-2 text-base text-yellow-600 font-medium">{item.unanswered}</td>
                                  <td className="py-2 px-2 text-base font-medium text-gray-800">{item.correct + item.incorrect + item.unanswered}</td>
                                  <td className="py-2 px-2 text-base font-medium text-gray-800">{item.accuracy}%</td>
                                </tr>
                              ))}
                              <tr className="border-t-2 border-gray-300 font-bold">
                                <td className="py-2 px-2 text-left text-base text-gray-800">Total</td>
                                <td className="py-2 px-2 text-base text-green-600">{module.correct}</td>
                                <td className="py-2 px-2 text-base text-red-500">{module.incorrect}</td>
                                <td className="py-2 px-2 text-base text-yellow-600">{module.totalQuestions - module.correct - module.incorrect}</td>
                                <td className="py-2 px-2 text-base text-gray-800">{module.totalQuestions}</td>
                                <td className="py-2 px-2 text-base text-gray-800">{module.totalQuestions > 0 ? Math.round((module.correct / 22) * 100) : 0}%</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </section>
        </div>

        {/* Question Review Section for PDF */}
        <div id="pdf-report-questions" className="mt-16">
          <h2 className="text-3xl font-bold font-zonaProRegular text-gray-800 mb-6 border-b border-gray-300 pb-2" style={{ color: '#0a1a2f' }}>
            Question Review Section
          </h2>
          {QUESTION_TABS.map(tab => (
            <div 
              key={tab.key} 
              className="mb-12"
              style={{
                breakInside: 'avoid',
                pageBreakInside: 'avoid',
                pageBreakBefore: 'auto',
              }}
            >
              <h3 
                className="text-2xl font-bold mb-4 bg-gray-100 p-3 rounded-md" 
                style={{ 
                  color: '#06bae4',
                  breakAfter: 'avoid',
                  pageBreakAfter: 'avoid',
                }}
              >
                {tab.label}
              </h3>
              <QuestionTable
                questions={questionsByTab?.[tab.key] || []}
                loading={false}
                error={false}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SATTestReportPDFLayout;

